// Catalog.ID Client — Personal Registration Wizard
// ============================================================================
// 9-step wizard (registration-restructure §6):
//   Terms → Keygen (throwaway credentials, silent) → Payment → Completion →
//   Device Setup → Email Validation → Phone Validation → Recovery Kit opt-in →
//   Finalize & Print
//
// Finalize-at-print (§3): registration runs on THROWAWAY credentials the user
// never sees; the final password, supersecret and XRPL master key are minted at
// the print step via the entropy ceremony (§13.1), then the shared finalize
// sequence (auth/finalize-credentials.ts) appends the XRPL key to the bag,
// re-encrypts it (step-up-gated replace-keybag), copies the new key onto this
// device and replaces the auth hash (set-authhash) — and everything the member
// keeps goes onto paper as ONE printed document set. Nothing is downloaded.
// A type-back gate (one supersecret line retyped from the paper) proves the
// print exists before the resume stash is wiped.
//
// Resume: all wizard state (throwaway credentials included) rides the
// localStorage onboarding stash (auth/onboarding-stash.ts) from keygen until
// the print gate. The Stripe redirect, a tab crash, or a browser restart all
// come back through it — if the session died, the throwaway password logs
// back in programmatically.
//
// Email and phone validation are mandatory (password-reset plan M1): the
// account only activates server-side once BOTH certs exist, and the Account
// Unlock Sheet sealed into the document set requires them.

import { Wizard, createElement, createProgressBar } from '@catalog-shell';
import type { WizardAPI, WizardStep, WizardStepContext } from '@catalog-shell';
import type { Content } from 'pdfmake/interfaces';
import { renderDeviceSetup, encryptLabelForDevice } from '../components/device-setup';
import { createEntropyCeremony, type EntropyCeremonyResult } from '../components/entropy-ceremony';
import { buildFrontPage } from '../components/booklet/front-page';
import { buildCredentialsPage } from '../components/booklet/credentials-page';
import { buildResetCardPage } from '../components/booklet/reset-card-page';
import { buildRecoveryCardPages, buildRecoveryCardCopySheets } from '../components/booklet/recovery-card-page';
import { buildCoverPage } from '../components/booklet/cover-page';
import { buildXrplKeyPage } from '../components/booklet/xrpl-key-page';
import { buildAuthenticatorKeyPage } from '../components/booklet/authenticator-key-page';
import {
    buildBitcashWalletPage, BITCASH_WALLET_PAGE_TITLE,
} from '../components/booklet/bitcash-wallet-page';
import { renderLoginCodeStep } from '../components/login-code-step';
import { printBooklet, type BookletPrintOptions, type CopySheet } from '../components/booklet/booklet';
import { createPrintButton, renderPrintStage } from '../components/print-button';
import { buildKitLayout, type KitLayout, recoverySheetSections, recoveryCopyTitles } from '../components/booklet/kit-layout';
import { bookletTitle, formatVersion, type AccountHeaderData } from '../components/booklet/shared';
import { TERMS_ASSET } from '../components/terms-asset';
import { createAgreementLink } from '../components/agreement-link';
import { documentSetPageCount, printerCheckboxLabel } from '../components/booklet/page-estimate';
import { createRecoveryKitCardFields } from '../components/recovery-kit-card-fields';
import { REGISTRATION_MIN_EXTRA_COPIES } from '../components/recovery-kit-presets';
import { generatePassword, computeAuthHash } from '../crypto/credentials';
import { generateSupersecret } from '../crypto/supersecret';
import { kemKeygen } from '../crypto/kem';
import { signingKeygen } from '../crypto/signing';
import { encryptKeyBag, getKeyBagEntry, isBagOnlyKeyId } from '../crypto/keybag';
import type { KeyBag } from '../crypto/keybag';
import {
    generateDeriveRoot, DERIVE_ROOT_KEY_ID, DERIVE_ROOT_ALGORITHM,
} from '../crypto/derive';
import {
    deriveXrplAddress, encodeXrplSeed, XRPL_MASTER_KEY_ID,
} from '../crypto/xrpl';
import {
    finalizeCredentials, rememberDeviceKey, deviceCopyTarget,
} from './finalize-credentials';
import {
    emailVerificationStep, phoneVerificationStep, type ClaimStepsConfig,
} from './claim-verification-steps';
import { buildRecoveryKit, type RecoveryKit } from '../crypto/recovery-kit';
import { collectBlobsForBundle } from '../crypto/bundle-blobs';
import type { BundleBlob } from '../crypto/bundle';
import * as blobApi from '../api/blobs';
import { decodeRecoveryKitEntry } from '../crypto/recovery-kit-entry';
import { base32Encode, base32Decode } from '../crypto/totp';
import * as aes from '../crypto/aes';
import * as accountApi from '../api/account';
import { isTotpRequired } from '../api/types';
import * as deviceApi from '../api/device';
import { session } from '../state/session';
import * as deviceStorage from '../state/device-storage';
import { keys } from '../state/keys';
import { identity } from '../state/identity';
import {
    freshNonce, getCancelUrl, getSuccessUrl, createPayment, pollUntilComplete,
    persistRegistrationState, clearRegistrationState,
    isTestHookActive, mintLocalTestProof, startPopupCheckoutAndAwaitProof,
    fetchRegistrationConfig, type DefaultTokenTerms,
} from './payment-flow';
import { paymentStepOffer, waivedContinueUsesCode } from './registration-gate';
import { inspectVoucher, purchaseVouchers } from '../api/voucher';
import { convertProofOnce, needsProofConversion } from './proof-conversion';
import {
    normalizeVoucherCode, deriveVoucherSecrets, voucherCodeHash, type VoucherSecrets, VOUCHER_CODE_PLACEHOLDER,
} from './voucher-code';
import { renderCheckedVoucher } from './checked-voucher-step';
import { mintVoucherBatch, voucherSpecOf, type MintedVoucher } from './voucher-mint';
import { createBitsField } from '../components/bits-field';
import { setUpWallet, walletSetupNotice, type WalletSetupResult } from './bits-release';
import { bitcashWorker } from '../state/bitcash-context';
import { planWalletSetup } from './wallet-setup-plan';
import {
    lockKeyFor, displayWalletKey, generateWalletKeyFrom, TYPE_CASH,
} from '../crypto/bitcash';
import { registrationWalletKey } from '../state/bitcash-wallets-store';
import { setDeviceSlot, rememberSlotKey, DEFAULT_TOKEN_SLOT } from '../state/device-slots';
import { mintedDefaultTokenEntry } from '../key-grant/bitcash-token-cache';
import { publishOutcomeNotice, publishReceiveAddress } from './receive-address-claim';
import { deriveInvitationSecrets } from './invitation-document';
import { inspectInvitation } from '../api/invitation';
import {
    loadOnboardingStash, saveOnboardingStash, readKitChoices, writeKitChoices,
    kitEntryFromChoices, kitExtraCopiesFor,
    readBitsStash,
} from './onboarding-stash';
import { completeOnboardingThenWipe } from './onboarding-completion';
import { finishLaterFrom, keepsRegistrationOnExit } from './wizard-exit';
import { isDashboardContext, dashboardHref } from '../state/dashboard-context';
import { nodeHostFor } from '../state/origins';

export interface RegistrationWizardCallbacks {
    onComplete: () => void;
    onCancel: () => void;
}

export interface RegistrationWizardOptions {
    /** True when resuming — after the Stripe redirect, or from the login
     *  screen's unfinished-registration banner. State restores from the
     *  onboarding stash and the wizard jumps to the right step. */
    resume?: boolean;
    /** A registration code the entry page (§14.2) already read with the node —
     *  unredeemed, and for a personal registration. The Payment step names it
     *  and offers Continue instead of asking for it again; the Completion step
     *  redeems it, so nothing is spent before the member has pressed through
     *  Terms, key generation and that Continue. */
    voucherCode?: string;
    /** A PERSONAL invitation code (`INV-…`, registration-restructure §14.3) the
     *  entry page has already had `inspect` confirm. Somebody bought this
     *  registration for this person, so there is nothing to pay: the Payment step
     *  becomes one line, and the completion step redeems the invitation instead
     *  of a voucher. Unlike a bare registration code this IS acted on without a
     *  second confirmation — it is addressed to whoever holds it, and it can only
     *  be completed by someone who verifies the channel it fixes. */
    invitationCode?: string;
}

export function renderRegistrationWizard(
    container: HTMLElement,
    callbacks: RegistrationWizardCallbacks,
    options: RegistrationWizardOptions = {},
): void {
    container.innerHTML = '';

    const wrapper = createElement('div', { className: 'cid-auth-container' });
    const card = createElement('div', { className: 'cid-auth-card cid-auth-card--wide' });
    wrapper.appendChild(card);
    container.appendChild(wrapper);

    // Shared mutable state — restored from the onboarding stash on resume.
    const shared: Record<string, unknown> = {};
    let resumeFinalize = false;
    if (options.resume) {
        const stash = loadOnboardingStash();
        if (stash && stash.registrationType === 'personal') {
            Object.assign(shared, stash.shared);
            if (stash.registrationId) shared['registrationId'] = stash.registrationId;
            if (stash.nonce) shared['nonce'] = stash.nonce;
            // The voucher outlives the proof, so a resume days later still has a
            // usable authorization even though the proof token expired long ago.
            if (stash.voucherCode) shared['voucherCode'] = stash.voucherCode;
            // The CODE, not either derivative: the lookup id and the fixed-value
            // salt are both one scrypt away from it (§14.2).
            if (stash.invitationCode) shared['invitationCode'] = stash.invitationCode;
            if (stash.resetCardSealedToken) shared['resetCardSealedToken'] = stash.resetCardSealedToken;
            resumeFinalize = stash.finalizeStarted === true;
        }
    }

    // A code the entry page already read with the node. Not a redemption: the
    // Payment step confirms it and the Completion step spends it. It rides the
    // stash with the rest of `shared` from key generation on, so a resumed
    // registration does not ask for it again.
    if (options.voucherCode) shared['checkedVoucherCode'] = options.voucherCode;
    if (options.invitationCode) shared['invitationCode'] = options.invitationCode;

    const steps = finishLaterFrom([
        termsStep(shared),
        keygenStep(shared),
        paymentStep(shared),
        completionStep(shared),
        deviceSetupStep(shared),
        emailVerificationStep(claimStepsConfig(shared)),
        phoneVerificationStep(claimStepsConfig(shared)),
        recoveryKitOptInStep(shared),
        finalizeStep(shared),
    ], 'completion');
    const wizard = new Wizard(card, {
        steps,
        onComplete: () => callbacks.onComplete(),
        onCancel: () => {
            // Before the account exists: an explicit cancel — discard the stash
            // so the payment proof can't be replayed and no throwaway
            // credentials linger.
            // From the step that creates the account on, the button says
            // "Finish later" and the stash stays — it is the only way back in, and
            // an account left unfinished is purged after 7 days (auth/wizard-exit.ts).
            if (!keepsRegistrationOnExit(steps, wizard.getCurrentIndex())) clearRegistrationState();
            callbacks.onCancel();
        },
        showCancel: true,
    });

    if (options.resume && (shared['registrationId'] || shared['cid'] || shared['invitationCode'])) {
        // Land on the right step. A dead session is revived with the throwaway
        // password first — every post-registration step needs authorization.
        void (async () => {
            await ensureResumedSession(shared);
            if (resumeFinalize || shared['phoneVerified']) {
                wizard.goTo(shared['kitDecided'] ? 'finalize' : 'recovery-kit');
            } else {
                // Completion's guard auto-advances when the CID already exists;
                // the following steps' own guards walk the user forward.
                wizard.goTo('completion');
            }
        })();
    }
}

// ============================================================================
// Stash + resume helpers
// ============================================================================

/** Write the current wizard state into the onboarding stash, preserving the
 *  payment-leg fields and finalize/reset-card markers unless overridden. */
function stashSync(shared: Record<string, unknown>, patch: {
    finalizeStarted?: boolean;
    resetCardSealedToken?: string;
    resetCardCreatedAt?: string;
    totpStored?: boolean;
} = {}): void {
    const prior = loadOnboardingStash();
    saveOnboardingStash({
        v: 1,
        savedAt: Date.now(),
        registrationType: 'personal',
        registrationId: (shared['registrationId'] as string | undefined) ?? prior?.registrationId,
        nonce: (shared['nonce'] as string | undefined) ?? prior?.nonce,
        voucherCode: (shared['voucherCode'] as string | undefined) ?? prior?.voucherCode,
        invitationCode: (shared['invitationCode'] as string | undefined) ?? prior?.invitationCode,
        personalSessionToken: prior?.personalSessionToken,
        finalizeStarted: patch.finalizeStarted ?? prior?.finalizeStarted,
        resetCardSealedToken: patch.resetCardSealedToken
            ?? (shared['resetCardSealedToken'] as string | undefined) ?? prior?.resetCardSealedToken,
        resetCardCreatedAt: patch.resetCardCreatedAt ?? prior?.resetCardCreatedAt,
        totpStored: patch.totpStored ?? prior?.totpStored,
        shared,
    });
}

/**
 * Make sure the resumed wizard is authorized and has keys in memory. The
 * localStorage session token usually survives; when it doesn't, log in with
 * the THROWAWAY password from the stash (the user never knew it — that is the
 * point of stashing it). After set-authhash the throwaway is dead, but that
 * call also deliberately kept this session alive.
 */
async function ensureResumedSession(shared: Record<string, unknown>): Promise<void> {
    const cid = shared['cid'] as string | undefined;
    if (!cid) return;

    if (shared['keyBag']) keys.load(shared['keyBag'] as KeyBag);

    try {
        await accountApi.getAccountInfo();
        return; // session alive
    } catch { /* dead session — revive below */ }

    try {
        const authHash = await computeAuthHash(String(shared['password'] ?? ''), cid);
        const resp = await accountApi.login({ cid, authHash });
        // A throwaway-password revival can never meet the Authenticator Key
        // gate — finalize stores the key with the gate OFF, and the optional
        // "Login code" step runs after the print gate in the same page
        // lifetime. If it somehow answers `totp_required` there is no session
        // to revive with, so fall through to the same handling as a failure.
        if (isTotpRequired(resp)) throw new Error('This account now requires an authenticator code to sign in');
        session.start(resp.sessionToken, {
            cid: resp.cid, accountType: resp.accountType, status: resp.status, sessionId: resp.sessionId,
        });
        identity.setActive({ cid: resp.cid, accountType: 'natural_person' });
        deviceStorage.setActiveCid(resp.cid);
    } catch {
        // Neither session nor throwaway login works (e.g. crash mid-finalize
        // after set-authhash and the session expired). The finalize step will
        // surface the API errors; the user's fallback is operator support.
    }
}

// ============================================================================
// Step 1: Terms
// ============================================================================

function termsStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'terms',
        label: 'Terms',
        render(container) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Membership Agreement' }));

            const text = createElement('div', { className: 'cid-wizard-terms' });
            text.textContent = 'By registering for a Catalog.ID identity, you agree to the Catalog.ID Membership Agreement. '
                + 'Your identity is generated entirely in your browser. The server never receives your private keys. '
                + 'Your credentials are printed — once — at the end of registration; you are solely responsible '
                + 'for keeping that printed document set safe. If you lose your supersecret and have no device with '
                + 'imported keys, your identity is irrecoverable by design.';
            step.appendChild(text);
            // The agreement itself, to read before accepting it (2026-10-15).
            step.appendChild(createAgreementLink(TERMS_ASSET));

            const checkRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const checkbox = document.createElement('input');
            checkbox.type = 'checkbox';
            checkbox.id = 'terms-accept';
            if (shared['termsAccepted']) checkbox.checked = true;
            const label = createElement('label', { textContent: 'I have read and accept the Membership Agreement' });
            label.setAttribute('for', 'terms-accept');
            checkRow.appendChild(checkbox);
            checkRow.appendChild(label);
            step.appendChild(checkRow);

            checkbox.addEventListener('change', () => { shared['termsAccepted'] = checkbox.checked; });

            // Registration ends at a printer — there is no download and no
            // second chance, so ask BEFORE any money or keys move.
            const printerRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const printerCheckbox = document.createElement('input');
            printerCheckbox.type = 'checkbox';
            printerCheckbox.id = 'terms-printer';
            if (shared['printerAvailable']) printerCheckbox.checked = true;
            const printerLabel = createElement('label', {
                // The size, so the member can tell whether their printer is up to it.
                textContent: printerCheckboxLabel('personal'),
            });
            printerLabel.setAttribute('for', 'terms-printer');
            printerRow.appendChild(printerCheckbox);
            printerRow.appendChild(printerLabel);
            step.appendChild(printerRow);

            printerCheckbox.addEventListener('change', () => { shared['printerAvailable'] = printerCheckbox.checked; });

            container.appendChild(step);
        },
        validate() {
            if (shared['termsAccepted'] !== true) return 'You must accept the Membership Agreement';
            return shared['printerAvailable'] === true
                || 'Confirm that you have a printer available — the document set is printed, never downloaded';
        },
    };
}

// ============================================================================
// Step 2: Identity Generation (throwaway credentials — user never sees them)
// ============================================================================

function keygenStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'keygen',
        label: 'Identity',
        hideNext: true,
        render(container, ctx) {
            // Already generated (e.g., user navigated back, or resumed)
            if (shared['keyBag']) {
                container.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: 'Identity material already generated.' }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Generating Your Identity' }));

            const progress = createProgressBar({ indeterminate: true, label: 'Generating keys...' });
            step.appendChild(progress.element);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            container.appendChild(step);

            void (async () => {
                try {
                    // Working credentials for the registration machinery only —
                    // finalize-at-print replaces both with ceremony-minted values
                    // the moment they go on paper (registration-restructure §3).
                    statusEl.textContent = 'Generating working credentials...';
                    shared['password'] = generatePassword();
                    shared['supersecret'] = generateSupersecret();

                    statusEl.textContent = 'Generating attr_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const attrKP = kemKeygen();

                    statusEl.textContent = 'Generating msg_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const msgKP = kemKeygen();

                    statusEl.textContent = 'Generating efs_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const efsKP = kemKeygen();

                    statusEl.textContent = 'Generating main_signing_key pair (Ed25519 + ML-DSA-65)...';
                    const sigKP = signingKeygen();

                    statusEl.textContent = 'Generating chat_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const chatEncKP = kemKeygen();

                    statusEl.textContent = 'Generating chat_sign key pair (Ed25519 + ML-DSA-65)...';
                    const chatSigKP = signingKeygen();

                    statusEl.textContent = 'Generating mail_encrypt key pair (X25519 + ML-KEM-1024)...';
                    const mailEncKP = kemKeygen();

                    statusEl.textContent = 'Generating mail_sign key pair (Ed25519 + ML-DSA-65)...';
                    const mailSigKP = signingKeygen();

                    // The derive root (EFS lineage-labels §5b). Minted at key-bag
                    // INITIALIZATION rather than lazily, because the lazy path costs
                    // a supersecret prompt mid-build: it is a bag mutation, and this
                    // is the one moment the bag is being written anyway. A 32-byte
                    // symmetric secret with no public half — bag-only, like
                    // xrpl_master and recovery_kit, so it is filtered out of the
                    // publicKeys payload below by isBagOnlyKeyId.
                    statusEl.textContent = 'Generating key-derivation root...';
                    const deriveRoot = generateDeriveRoot();

                    // NO XRPL master key here (round 6, §13.1): it is minted by
                    // the entropy ceremony at the print step, in front of the
                    // member, and appended to the bag by the finalize helper.
                    const keyBag: KeyBag = {
                        entries: [
                            { keyId: 'attr_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(attrKP.secretKey) },
                            { keyId: 'msg_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(msgKP.secretKey) },
                            { keyId: 'efs_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(efsKP.secretKey) },
                            { keyId: 'main_signing_key', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(sigKP.secretSeed) },
                            { keyId: 'chat_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(chatEncKP.secretKey) },
                            { keyId: 'chat_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(chatSigKP.secretSeed) },
                            { keyId: 'mail_encrypt', algorithm: 'X25519-MLKem1024', privateKey: aes.toBase64(mailEncKP.secretKey) },
                            { keyId: 'mail_sign', algorithm: 'Ed25519-MLDsa65', privateKey: aes.toBase64(mailSigKP.secretSeed) },
                            { keyId: DERIVE_ROOT_KEY_ID, algorithm: DERIVE_ROOT_ALGORITHM, privateKey: aes.toBase64(deriveRoot) },
                        ],
                    };
                    shared['keyBag'] = keyBag;
                    shared['attrPublicKey'] = aes.toBase64(attrKP.publicKey);
                    // The public halves of every generated key, for the LOCAL surfaces —
                    // the booklet's key material and the device bundle's key_chain.
                    // `chat_sign` and `mail_sign` are present here but are filtered out
                    // of the server registration payload below: they are BAG-ONLY keys
                    // (see BAG_ONLY_KEY_IDS), whose public halves publish on the
                    // `registry_signing_key` CLAIM track under the `chat_root` /
                    // `mail_root` key classes (chat migration §3, mail migration §5.2),
                    // never as `accountpublickey` rows.
                    shared['publicKeys'] = {
                        attr_encrypt: aes.toBase64(attrKP.publicKey),
                        msg_encrypt: aes.toBase64(msgKP.publicKey),
                        efs_encrypt: aes.toBase64(efsKP.publicKey),
                        main_signing_key: aes.toBase64(sigKP.publicKey),
                        chat_encrypt: aes.toBase64(chatEncKP.publicKey),
                        chat_sign: aes.toBase64(chatSigKP.publicKey),
                        mail_encrypt: aes.toBase64(mailEncKP.publicKey),
                        mail_sign: aes.toBase64(mailSigKP.publicKey),
                    };

                    statusEl.textContent = 'Encrypting key bag (Argon2id)...';
                    progress.setLabel('Encrypting key bag...');
                    const encrypted = await encryptKeyBag(keyBag, shared['supersecret'] as string);
                    shared['encryptedKeyBag'] = encrypted;

                    // authHash needs the CID (Argon2id(password, SHA-256(cid))) — deferred to
                    // the completion step, after /register/begin has assigned us a CID.

                    // From this point the stash carries everything needed to resume.
                    stashSync(shared);

                    progress.setIndeterminate(false);
                    progress.setValue(100);
                    progress.setVariant('success');
                    progress.setLabel('Keys generated');
                    statusEl.textContent = '';

                    setTimeout(() => ctx.wizard.next(), 600);
                } catch (err) {
                    progress.setIndeterminate(false);
                    progress.setVariant('error');
                    progress.setLabel('Failed');
                    statusEl.textContent = err instanceof Error ? err.message : 'Key generation failed';
                    statusEl.className = 'cid-pane-error cid-pane-error--visible';
                }
            })();
        },
    };
}

// ============================================================================
// Step 3: Payment (Stripe redirect via Worker)
// ============================================================================

/**
 * Hand a voucher code to the Completion step, which redeems it. A synthetic
 * registrationId keeps the stash's payment-leg shape intact (restore requires
 * both id and nonce) without pretending a Stripe payment happened. The nonce is
 * unused on this path — the voucher itself is the reservation anchor server-side.
 */
function proceedWithVoucher(shared: Record<string, unknown>, code: string, wizard: WizardAPI): void {
    const nonce = freshNonce();
    const registrationId = `voucher_${nonce.slice(0, 16)}`;
    shared['nonce'] = nonce;
    shared['registrationId'] = registrationId;
    shared['voucherCode'] = code;
    persistRegistrationState({
        registrationType: 'personal',
        registrationId,
        nonce,
        shared,
    });
    wizard.goTo('completion');
}

/** Exported for its tests (`checked-voucher-payment.test.ts`). */
export function paymentStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'payment',
        label: 'Payment',
        hideNext: true,
        render(container, ctx) {
            // An INVITATION already paid for this registration
            // (registration-restructure §14.3), so there is nothing to settle and
            // nothing to choose. One line and a button — showing a fee, a Stripe
            // control and a voucher field to somebody holding a paid invitation
            // would only invite them to pay twice.
            if (shared['invitationCode']) {
                const covered = createElement('div', { className: 'cid-wizard-step' });
                covered.appendChild(createElement('h3', {
                    className: 'cid-wizard-heading', textContent: 'Registration Fee',
                }));
                covered.appendChild(createElement('p', {
                    className: 'cid-wizard-text',
                    textContent: 'Your invitation covers the registration fee.',
                }));
                const continueBtn = createElement('button', {
                    className: 'cs-btn cs-btn--yes cid-auth-submit',
                    textContent: 'Continue',
                });
                continueBtn.addEventListener('click', () => ctx.wizard.goTo('completion'));
                covered.appendChild(continueBtn);
                container.appendChild(covered);
                return;
            }

            // A code already read with the node (the entry page): name it and go
            // on, rather than ask for it again (checked-voucher-step.ts).
            const checked = shared['checkedVoucherCode'] as string | undefined;
            if (checked) {
                renderCheckedVoucher(container, {
                    code: checked,
                    onContinue: () => proceedWithVoucher(shared, checked, ctx.wizard),
                    onUseAnother: () => {
                        delete shared['checkedVoucherCode'];
                        // Back from Completion, it was already taken; the field
                        // the member asked for starts empty.
                        if (shared['voucherCode'] === checked) delete shared['voucherCode'];
                        ctx.wizard.goTo('payment');
                    },
                });
                return;
            }

            // Inside the dashboard the client is iframed and Stripe hosted
            // Checkout refuses to run in an iframe, so we open it in a popup and
            // poll payment-status in place. A standalone browser keeps the
            // full-page redirect + resume-on-return flow (unchanged).
            const dashboard = isDashboardContext();

            const step = createElement('div', { className: 'cid-wizard-step' });

            const heading = createElement('h3', { className: 'cid-wizard-heading', textContent: 'Registration Fee' });
            step.appendChild(heading);
            const feeText = createElement('p', {
                className: 'cid-wizard-text',
                textContent: dashboard
                    ? 'A one-time registration fee is required to create your identity. Payment opens in a separate Stripe window; this page continues automatically once payment confirms.'
                    : 'A one-time registration fee is required to create your identity. Payment is handled by Stripe; you will be redirected and brought back here once payment confirms.',
            });
            step.appendChild(feeText);

            // Bits on the wallet this registration mints (D1): the member
            // chooses the amount here exactly as a voucher buyer does in the
            // storefront. The bounds arrive with the config below, the euro
            // rate from the Worker; bits are mandatory on every checkout.
            const bitsField = createBitsField({
                label: 'Bits on your new wallet',
                hint: 'spending money for services, released into the wallet printed in your kit at the end of registration.',
            });
            step.appendChild(bitsField.row);

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            step.appendChild(errorEl);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            let idleLabel = isTestHookActive() ? 'Pay (test hook — skip Stripe)' : 'Continue to payment';
            const payBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: idleLabel,
            });

            // Runtime payment mode (Payment:Mode on the server): the button stays
            // disabled until the config resolves so a click can't race it. Fails
            // closed — an unreachable config endpoint keeps payment required.
            let paymentWaived = false;
            payBtn.setAttribute('disabled', '');
            void (async () => {
                const cfg = await fetchRegistrationConfig();
                const offer = paymentStepOffer(cfg);
                paymentWaived = offer.waivedContinue;
                bitsField.setBounds(cfg.bits);
                if (!offer.pay && !offer.waivedContinue) {
                    // The registration gate (docs/cid-governance.md M12): this node
                    // registers by invitation only, or could not say — a code is
                    // the one way on. No checkout, no bits to choose, no storefront.
                    heading.textContent = 'Registration code';
                    feeText.textContent = offer.notice ?? '';
                    payBtn.remove();
                    bitsField.remove();
                    showCodeOnly();
                    return;
                }
                if (paymentWaived) {
                    feeText.textContent = 'Registration on this server is currently free — no payment is required.';
                    idleLabel = 'Continue';
                    payBtn.textContent = idleLabel;
                    // Nothing is sold on a waived node — no storefront, no bits to
                    // choose (D9) — but a code still redeems: its lock holds the
                    // bits it was minted with.
                    showWaivedCode();
                    bitsField.remove();
                }
                payBtn.removeAttribute('disabled');
                // The pilot Worker's euro rate is gone with it (apps/bitcash/docs/plans/bitcash-v2.md
                // M12); the commercial track's sale configuration replaces it (M16),
                // and the label says "100 000 bits" and nothing else, which is still true.
            })();

            payBtn.addEventListener('click', async () => {
                // On a waived node a code in the field is redeemed, never skipped.
                if (paymentWaived && waivedContinueUsesCode(codeInput.value)) {
                    void redeemTypedCode();
                    return;
                }
                payBtn.setAttribute('disabled', '');
                payBtn.textContent = paymentWaived ? 'Continuing...' : 'Preparing payment...';
                errorEl.classList.remove('cid-pane-error--visible');
                statusEl.textContent = '';

                const nonce = freshNonce();

                // Payment waived on this node (staging posture): no Stripe, no
                // proof — the server accepts the registration on the nonce alone
                // (its 32 random bytes are the idempotency anchor). No navigation
                // happens, so dashboard and standalone both jump straight on.
                if (paymentWaived) {
                    const registrationId = `waived_${nonce.slice(0, 16)}`;
                    shared['nonce'] = nonce;
                    shared['proofToken'] = '';
                    shared['paymentWaived'] = true;
                    shared['registrationId'] = registrationId;
                    persistRegistrationState({
                        registrationType: 'personal',
                        registrationId,
                        nonce,
                        shared,
                    });
                    ctx.wizard.goTo('completion');
                    return;
                }

                const chosen = bitsField.read();
                if (chosen.problem !== null) { showError(new Error(chosen.problem)); return; }
                const bits = chosen.bits;

                // ── mint the voucher BEFORE paying (registration-wallets §4.3) ──
                // "Pay and continue" buys exactly one voucher and redeems it on
                // the spot, so it mints exactly one code here — before the card
                // is charged, because the webhook creates the lock against the
                // key this code derives and has nothing to create it against
                // otherwise. The CODE goes into the stash: it is what redeems
                // the voucher at completion AND what signs the release at the
                // print step, and a resume re-derives everything from it.
                let minted: MintedVoucher;
                try {
                    payBtn.textContent = 'Preparing your registration…';
                    const batch = await mintVoucherBatch({
                        registrationType: 'personal', quantity: 1, bits,
                    });
                    minted = batch.vouchers[0];
                    shared['voucherCode'] = minted.code;
                } catch (err) {
                    showError(err);
                    return;
                }

                // Test hook (inert in prod): mint a local proof and continue without Stripe.
                if (isTestHookActive()) {
                    try {
                        const proofToken = await mintLocalTestProof(
                            'personal', nonce, 1, [voucherCodeHash(minted.lookupIdHex)]);
                        const registrationId = `test_${nonce.slice(0, 16)}`;
                        shared['nonce'] = nonce;
                        shared['proofToken'] = proofToken;
                        shared['registrationId'] = registrationId;
                        persistRegistrationState({
                            registrationType: 'personal',
                            registrationId,
                            nonce,
                            shared,
                        });
                        if (dashboard) {
                            ctx.wizard.goTo('completion');
                        } else {
                            location.assign(`${window.location.origin}/auth/registration-complete?registrationId=${encodeURIComponent(registrationId)}`);
                        }
                    } catch (err) {
                        showError(err);
                    }
                    return;
                }

                if (dashboard) {
                    // The popup can no longer be opened inside the click's own
                    // user activation — the mint above is asynchronous. Browsers
                    // still allow a `window.open` a short time after a click on
                    // the same task chain; if one is blocked, the helper below
                    // reports it as "allow pop-ups" rather than failing silently.
                    const popup = window.open('', 'cid-stripe-checkout', 'width=480,height=760');
                    await runDashboardCheckout(nonce, popup, minted, bits);
                    return;
                }

                // Standalone browser: full-page redirect to Stripe; resume on return.
                try {
                    const created = await createPayment({
                        registrationType: 'personal',
                        nonce,
                        quantity: 1,
                        bits,
                        vouchers: [voucherSpecOf(minted)],
                        successUrl: getSuccessUrl(),
                        cancelUrl: getCancelUrl(),
                    });
                    persistRegistrationState({
                        registrationType: 'personal',
                        registrationId: created.registrationId,
                        nonce,
                        shared,
                    });
                    location.assign(created.checkoutUrl);
                } catch (err) {
                    showError(err);
                }
            });
            step.appendChild(payBtn);

            // ── Voucher alternatives (registration-restructure §7.3) ─────────
            // Two of the three entry-dialog options live here, where the fee is
            // actually being settled: redeem a code you already hold, or go buy
            // codes for later / for someone else. The third — "pay and continue"
            // — is payBtn above, which now buys exactly one voucher and redeems
            // it on the spot (the conversion happens in the completion step, so
            // the Stripe popup, the Stripe redirect and the e2e test hook all
            // converge on one redemption path).
            const voucherSection = createElement('div', { className: 'cid-wizard-step' });

            const voucherIntro = createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'Already have a voucher code? Enter it here instead of paying.',
            });
            voucherSection.appendChild(voucherIntro);

            const codeWrapper = createElement('div', { className: 'cid-auth-field' });
            const codeInput = createElement('input', { className: 'cid-auth-input' });
            codeInput.type = 'text';
            // Back from Completion, the code already taken.
            codeInput.value = (shared['voucherCode'] as string | undefined) ?? '';
            codeInput.placeholder = VOUCHER_CODE_PLACEHOLDER;
            codeInput.autocomplete = 'off';
            codeInput.spellcheck = false;
            codeWrapper.appendChild(codeInput);
            voucherSection.appendChild(codeWrapper);

            const redeemBtn = createElement('button', {
                className: 'cs-btn cs-btn--default cid-auth-submit',
                textContent: 'Use voucher',
            });
            redeemBtn.addEventListener('click', () => { void redeemTypedCode(); });
            codeInput.addEventListener('keydown', (e) => {
                if ((e as KeyboardEvent).key === 'Enter') { e.preventDefault(); void redeemTypedCode(); }
            });
            voucherSection.appendChild(redeemBtn);

            const buyRow = createElement('div', { className: 'cid-auth-link-row' });
            const buyLink = createElement('a', {
                className: 'cid-auth-link',
                textContent: 'Buy voucher codes for later or for someone else',
            });
            buyLink.href = dashboardHref('/vouchers');
            // New tab: the wizard is mid-flight (keys generated, stash written), and
            // navigating away in place would force a resume for no reason. In the
            // dashboard the client is iframed, where an in-place navigation would
            // also take the whole pane with it.
            buyLink.target = '_blank';
            buyLink.rel = 'noopener';
            buyRow.appendChild(buyLink);
            voucherSection.appendChild(buyRow);

            step.appendChild(voucherSection);

            container.appendChild(step);

            // ── helpers (hoisted) ───────────────────────────────────────────

            /** A waived open node: the code section without the storefront. */
            function showWaivedCode(): void {
                voucherIntro.textContent = 'Have a registration code? Enter it to receive the bits '
                    + 'that come with it — or just Continue.';
                buyRow.remove();
            }

            /** The registration gate (M12): the code section alone — no storefront. */
            function showCodeOnly(): void {
                voucherIntro.textContent = 'Enter the registration code you were given.';
                buyRow.remove();
            }

            /** Validate a typed code client-side, then hand it to the completion
             *  step. The server re-validates everything; this only saves a round
             *  trip on an obvious typo. */
            async function redeemTypedCode(): Promise<void> {
                const normalized = normalizeVoucherCode(codeInput.value);
                if (!normalized) {
                    errorEl.textContent = 'That does not look like a voucher code. Codes start with RGV-.';
                    errorEl.classList.add('cid-pane-error--visible');
                    return;
                }
                errorEl.classList.remove('cid-pane-error--visible');
                redeemBtn.setAttribute('disabled', '');
                statusEl.textContent = 'Redeeming voucher…';
                proceedWithVoucher(shared, normalized, ctx.wizard);
            }

            function showError(err: unknown): void {
                payBtn.removeAttribute('disabled');
                payBtn.textContent = idleLabel;
                statusEl.textContent = '';
                errorEl.textContent = err instanceof Error ? err.message : 'Could not start payment';
                errorEl.classList.add('cid-pane-error--visible');
            }

            async function runDashboardCheckout(
                nonce: string, popup: Window | null, minted: MintedVoucher, bits: number,
            ): Promise<void> {
                const controller = new AbortController();
                payBtn.textContent = 'Waiting for payment…';
                statusEl.textContent = 'Complete your payment in the Stripe window.';

                const cancelBtn = createElement('button', {
                    className: 'cs-btn cs-btn--default cid-auth-submit',
                    textContent: 'Cancel payment',
                });
                cancelBtn.addEventListener('click', () => {
                    controller.abort();
                    try { popup?.close(); } catch { /* ignore */ }
                });
                step.appendChild(cancelBtn);

                const result = await startPopupCheckoutAndAwaitProof({
                    popup,
                    registrationType: 'personal',
                    nonce,
                    quantity: 1,
                    bits,
                    vouchers: [voucherSpecOf(minted)],
                    abortSignal: controller.signal,
                    onStatus: (label) => { statusEl.textContent = label; },
                });

                cancelBtn.remove();

                if (result.status === 'complete') {
                    statusEl.textContent = 'Payment confirmed. Finalising your registration…';
                    shared['nonce'] = nonce;
                    shared['proofToken'] = result.proofToken;
                    shared['registrationId'] = result.registrationId;
                    stashSync(shared);
                    ctx.wizard.goTo('completion');
                    return;
                }
                if (result.status === 'cancelled') {
                    payBtn.removeAttribute('disabled');
                    payBtn.textContent = idleLabel;
                    statusEl.textContent = 'Payment cancelled. You can try again.';
                    return;
                }
                showError(new Error(result.error));
            }
        },
    };
}

// ============================================================================
// Step 4: Completion (poll proof + call /register + session startup)
// ============================================================================

function completionStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'completion',
        label: 'Complete',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            // Already completed (e.g., resumed after the CID was assigned)
            if (shared['cid']) {
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Confirming Your Registration' }));

            const progress = createProgressBar({ indeterminate: true, label: 'Waiting for payment confirmation...' });
            step.appendChild(progress.element);

            const statusEl = createElement('p', { className: 'cid-wizard-status' });
            step.appendChild(statusEl);

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            step.appendChild(errorEl);

            container.appendChild(step);

            void (async () => {
                try {
                    // ── invitation-backed registration (§14.3) ──────────────
                    // No proof, no voucher code and no registrationId: the
                    // invitation names the voucher it reserved, and the node
                    // resolves it from the lookup id derived here. Everything
                    // downstream — the reserved CID, the payment binding, the
                    // idempotency slot — is anchored on that voucher exactly as a
                    // typed code would be, so a re-begin returns the same CID.
                    const invitationCode = shared['invitationCode'] as string | undefined;
                    if (invitationCode) {
                        await completeThroughInvitation(invitationCode);
                        return;
                    }

                    const registrationId = shared['registrationId'] as string | undefined;
                    const nonce = shared['nonce'] as string | undefined;
                    if (!registrationId || !nonce) {
                        throw new Error('Lost payment context. Please restart registration from the beginning.');
                    }

                    // Vouchers unify payment (registration-restructure §7): whichever
                    // way the fee was settled — Stripe popup, Stripe redirect, or the
                    // e2e test hook — the proof is converted into a voucher here and
                    // the registration redeems THAT. One redemption path, and the
                    // durable voucher row replaces the proof's one-hour expiry cliff.
                    // Conversion is idempotent on the Stripe session, so a crash
                    // between paying and stashing re-derives the very same code.
                    // The CODE, not the lookup id: the code is what the print
                    // step's release needs, and everything else derives from it
                    // (one scrypt, run just below). "Pay and continue" stashed
                    // it before the checkout opened; a typed voucher stashed it
                    // at the payment step.
                    const voucherCode = shared['voucherCode'] as string | undefined;
                    let proofToken = shared['proofToken'] as string | undefined;

                    // The node is addressed by the LOOKUP ID, never the code
                    // (§4.1, D16): the code opens the Worker lock holding this
                    // voucher's bits, and a node able to see it could open that
                    // lock. Derived ONCE, up front: the conversion below needs
                    // it for its fallback and the reservation needs it after.
                    const voucherLookupId = voucherCode
                        ? (await deriveVoucherSecrets(voucherCode)).lookupIdHex : undefined;

                    // A PAID leg (Stripe popup, Stripe redirect, the e2e test
                    // hook) has a proof that must become a voucher ROW before
                    // the node can resolve that lookup id. The code was minted
                    // and stashed BEFORE the card was charged (§4.3), so its
                    // presence says nothing about whether the row exists yet —
                    // a typed voucher and a waived registration have no proof
                    // to convert. See `proof-conversion.ts` for the legs and the
                    // resume/retry rules.
                    if (needsProofConversion(shared, registrationId)) {
                        if (!voucherCode || !voucherLookupId) {
                            throw new Error('This browser has lost the registration code it bought. '
                                + 'It was shown nowhere else — contact support with your payment '
                                + 'reference.');
                        }
                        if (!proofToken) {
                            statusEl.textContent = `Polling payment status for ${registrationId}...`;
                            const result = await pollUntilComplete(registrationId, nonce, (r, attempt) => {
                                statusEl.textContent = `Status check ${attempt}: ${r.status}${r.status === 'error' ? ' — ' + r.error : ''}`;
                            });
                            if (result.status === 'error') throw new Error(result.error);
                            if (result.status !== 'complete') throw new Error('Payment is taking longer than expected. Refresh this page within an hour to retry.');
                            proofToken = result.proofToken;
                            shared['proofToken'] = proofToken;
                        }
                        const paidProof = proofToken;
                        statusEl.textContent = 'Issuing your registration voucher...';
                        progress.setLabel('Issuing voucher...');
                        // Conversion returns no codes (§4.3): this client
                        // committed only to the hash of the code in its stash.
                        await convertProofOnce({
                            purchase: () => purchaseVouchers({
                                paymentProofToken: paidProof,
                                nonce,
                                registrationType: 'personal',
                            }),
                            // A crash between a successful conversion and the
                            // flag write below, resumed after the proof's hour:
                            // the row is there and `inspect` resolves it.
                            rowExists: async () => { await inspectVoucher(voucherLookupId); return true; },
                        });
                        shared['voucherConverted'] = true;
                        // Stash before spending it: a retry or a resume must go
                        // straight to the reservation, never re-send the proof.
                        stashSync(shared);
                    }

                    statusEl.textContent = 'Reserving your Catalog.ID...';
                    progress.setLabel('Reserving CID...');
                    const authorization = voucherLookupId
                        ? { voucherLookupId }
                        : { paymentProofToken: proofToken ?? '', nonce };
                    const reservation = await accountApi.beginRegister(authorization);

                    await finishRegistration(reservation.cid, authorization);
                } catch (err) {
                    progress.setIndeterminate(false);
                    progress.setVariant('error');
                    progress.setLabel('Registration failed');
                    statusEl.textContent = '';
                    errorEl.textContent = err instanceof Error ? err.message : 'Registration failed';
                    errorEl.classList.add('cid-pane-error--visible');

                    const retryBtn = createElement('button', {
                        className: 'cs-btn cs-btn--default cid-auth-submit',
                        textContent: 'Retry',
                    });
                    retryBtn.addEventListener('click', () => {
                        container.innerHTML = '';
                        completionStep(shared).render(container, ctx);
                    });
                    step.appendChild(retryBtn);
                }
            })();

            // ── helpers (hoisted) ───────────────────────────────────────────

            /**
             * Redeem a personal invitation (registration-restructure §14.3).
             *
             * One scrypt over the code yields BOTH the lookup id the node is
             * addressed by and the fixed-value salt the claim steps hand to the
             * validator — so it is run once, here, and both halves are stashed.
             * `inspect` then says which channels the invitation fixes; it has to
             * happen BEFORE /register, since a redeemed invitation is no longer
             * inspectable.
             */
            async function completeThroughInvitation(code: string): Promise<void> {
                statusEl.textContent = 'Reading your invitation code...';
                progress.setLabel('Reading your invitation...');
                const secrets = await deriveInvitationSecrets(code);
                shared['invitationValueSalt'] = secrets.saltHex;

                const info = await inspectInvitation(secrets.lookupIdHex);
                // WHETHER each channel is fixed, never the values: those are
                // printed on the card and typed by the member (§14.2).
                shared['invitationEmailFixed'] = info.emailFixed;
                shared['invitationPhoneFixed'] = info.phoneFixed;
                stashSync(shared);

                statusEl.textContent = 'Reserving your Catalog.ID...';
                progress.setLabel('Reserving CID...');
                const reservation = await accountApi.beginRegister({ invitationLookupId: secrets.lookupIdHex });
                await finishRegistration(reservation.cid, { invitationLookupId: secrets.lookupIdHex });
            }

            /**
             * The half of completion that is the same however the fee was
             * settled: hash the throwaway password against the reserved CID,
             * create the account, and start the session. `authorization` is
             * whichever of the three the node is being shown.
             */
            async function finishRegistration(
                cid: string,
                authorization: {
                    voucherLookupId?: string; paymentProofToken?: string; nonce?: string;
                    invitationLookupId?: string;
                },
            ): Promise<void> {
                statusEl.textContent = 'Computing auth hash (Argon2id)...';
                const authHash = await computeAuthHash(shared['password'] as string, cid);

                statusEl.textContent = 'Registering identity with the server...';
                progress.setLabel('Registering...');

                const encrypted = shared['encryptedKeyBag'] as { keySalt: string; entries: { keyId: string; keyIndex: number; algorithm: string; encryptedPrivateKey: string; nonce: string }[] };
                const publicKeys = shared['publicKeys'] as Record<string, string>;
                const keyBag = shared['keyBag'] as KeyBag;

                const resp = await accountApi.register({
                    cid,
                    authHash,
                    keySalt: encrypted.keySalt,
                    // Bag-only keys (chat_sign, …) carry no accountpublickey row —
                    // the server's keybag consistency check skip-lists them.
                    publicKeys: keyBag.entries
                        .filter(e => !isBagOnlyKeyId(e.keyId))
                        .map((e) => ({
                            keyId: e.keyId,
                            algorithm: e.algorithm,
                            publicKey: publicKeys[e.keyId] ?? '',
                        })),
                    keyBagEntries: encrypted.entries,
                    ...authorization,
                });

                shared['cid'] = resp.cid;
                session.start(resp.sessionToken, { cid: resp.cid, accountType: 'natural_person', status: 'pending', sessionId: resp.sessionId });
                identity.setActive({ cid: resp.cid, accountType: 'natural_person' });
                deviceStorage.setActiveCid(resp.cid);
                keys.load(keyBag);

                // The stash lives on until the print gate — it is the only
                // recovery path for everything between here and the paper.
                stashSync(shared);

                progress.setIndeterminate(false);
                progress.setValue(100);
                progress.setVariant('success');
                progress.setLabel(`Identity ${resp.cid} created`);
                statusEl.textContent = '';

                step.appendChild(createElement('p', { className: 'cid-wizard-cid', textContent: `Your Catalog.ID: ${resp.cid}` }));

                setTimeout(() => ctx.wizard.next(), 1200);
            }
        },
    };
}

// ============================================================================
// Step 5: Device Setup (before the print — device-held keys are the crash net)
// ============================================================================

function deviceSetupStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'device',
        label: 'Device',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            if (shared['deviceSetupComplete']) {
                container.appendChild(createElement('p', { className: 'cid-wizard-text', textContent: 'Device configured.' }));
                setTimeout(() => ctx.wizard.next(), 0);
                return;
            }

            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Device Setup' }));

            renderDeviceSetup(step, shared['keyBag'] as KeyBag, shared['cid'] as string, {
                onSetup: async (
                    params: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[0],
                    manifest: Parameters<import('../components/device-setup').DeviceSetupCallbacks['onSetup']>[1],
                ) => {
                    // `label` is plaintext — encrypt it here; never forward it as-is.
                    const { label: plainLabel, ...rest } = params;
                    const cid = shared['cid'] as string;
                    const labelFields = await encryptLabelForDevice(cid, plainLabel);
                    const resp = await deviceApi.setupDevice({ ...rest, ...labelFields });
                    deviceStorage.setDeviceId(cid, resp.deviceId);
                    // Keep the DEK for the finalize step: the ceremony mints
                    // `xrpl_master` AFTER this point, and its device copy has to
                    // be encrypted under this same key (§11 trap, §13.1) — and
                    // under the same key SELECTION, so a key the member just
                    // declined is not written here a minute later
                    // (device-setup-restructure §8, §7.3).
                    rememberDeviceKey(shared, cid, params.deviceEncryptionKey, manifest);
                    // …and give the device-slot store the same key, so the
                    // default token the print step is about to mint can be
                    // cached under it (registration-wallets §4.12).
                    rememberSlotKey(cid, aes.fromBase64(params.deviceEncryptionKey));
                    shared['deviceSetupComplete'] = true;
                    stashSync(shared);
                    ctx.wizard.next();
                },
                onSkip: () => {
                    deviceStorage.setNoLocalStorageMode(shared['cid'] as string);
                    shared['deviceSetupComplete'] = true;
                    stashSync(shared);
                    ctx.wizard.next();
                },
            }, { confirmLabel: 'Configure device and continue' });

            container.appendChild(step);
        },
    };
}

// ============================================================================
// Steps 6 + 7: Email + Phone Validation
// ============================================================================
// Both steps live in auth/claim-verification-steps.ts since M13 — the org
// wizard's founding delegate and the invited-delegate activation wizard run the
// SAME sequence, under their own session and attr key (§13.3). The
// configuration below is what this wizard used to do inline: the personal
// account's own attr_encrypt pair out of the bag it just generated.

function claimStepsConfig(shared: Record<string, unknown>): ClaimStepsConfig {
    return {
        shared,
        stashSync: () => stashSync(shared),
        attrPublicKey: () => shared['attrPublicKey'] as string,
        attrPrivateKey: () => getKeyBagEntry(shared['keyBag'] as KeyBag, 'attr_encrypt')!.privateKey,
        // A personal INVITATION (§14.3) may fix the email and/or the phone: the
        // buyer paid for this registration and addressed it to one mailbox or
        // number, and only somebody who can verify it may finish. Exactly the
        // wiring the activation wizard uses — the flags only add a note, the
        // fields stay empty and editable, and the VALIDATOR enforces the match
        // against the digest, using the salt derived from the code below.
        emailFixed: () => shared['invitationEmailFixed'] === true,
        phoneFixed: () => shared['invitationPhoneFixed'] === true,
        valueSalt: () => shared['invitationValueSalt'] as string | undefined,
    };
}

// ============================================================================
// Step 8: Recovery Kit opt-in (decision only — sheets split the FINAL
// supersecret, so the kit itself is created inside the finalize step)
// ============================================================================

/** Profile a personal kit falls back to when the stash names none (Addendum §7). */
const PERSONAL_KIT_DEFAULTS = { thresholdK: 2, totalSharesN: 3 };

function recoveryKitOptInStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'recovery-kit',
        label: 'Recovery',
        hideBack: true,
        nextLabel: 'Continue',
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });

            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Account Recovery Sheets' }));
            step.appendChild(createElement('p', {
                className: 'cid-wizard-text',
                textContent: 'Recovery sheets split your supersecret across separate printed pages: any two of '
                    + 'the three together recover your account if the supersecret page is ever lost, while a '
                    + 'single sheet reveals nothing. The sheets in your document set stay with it as your own '
                    + 'safe copy; you hand out only the additional copies printed after it, each to the person '
                    + 'or place its purpose names. Any sheet can be printed again later from Settings.',
            }));

            const stored = readKitChoices(shared);

            // Not optional, and the pattern is the member's own choice — none is
            // preselected — with at least one copy of every sheet to hand out
            // (staging test 2026-10-15).
            const fields = createRecoveryKitCardFields({
                kind: 'personal',
                idPrefix: 'kit',
                presetId: stored.presetId,
                labels: stored.labels.length > 0 ? stored.labels : undefined,
                extraCopies: stored.extraCopies.length > 0 ? stored.extraCopies : undefined,
                requirePresetChoice: true,
                minExtraCopies: REGISTRATION_MIN_EXTRA_COPIES,
                onPresetChange: () => ctx.wizard.setNextEnabled(true),
            });
            step.appendChild(fields.element);
            if (!fields.hasChoice()) ctx.wizard.setNextEnabled(false, 'Choose how your recovery sheets are distributed first');

            container.appendChild(step);

            this.validate = () => {
                if (!fields.hasChoice()) return 'Choose how your recovery sheets are distributed first';
                const picked = fields.read();
                writeKitChoices(shared, {
                    optIn: true,
                    presetId: picked.preset.id,
                    thresholdK: picked.preset.profile.thresholdK,
                    totalSharesN: picked.preset.profile.totalSharesN,
                    labels: picked.labels,
                    extraCopies: picked.extraCopies,
                });
                shared['kitDecided'] = true;
                stashSync(shared);
                return true;
            };
        },
        validate() { return true; }, // replaced in render()
    };
}

// ============================================================================
// Step 9: Finalize & Print (entropy ceremony → re-encrypt + set-authhash →
// document-set print → type-back gate → stash wipe)
// ============================================================================

function finalizeStep(shared: Record<string, unknown>): WizardStep {
    return {
        id: 'finalize',
        label: 'Print',
        hideNext: true,
        hideBack: true,
        render(container, ctx) {
            const step = createElement('div', { className: 'cid-wizard-step' });
            step.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Create & Print Your Credentials' }));

            const errorEl = createElement('div', { className: 'cid-pane-error' });
            const statusEl = createElement('p', { className: 'cid-wizard-status' });

            let ceremonyResult: EntropyCeremonyResult | null = null;
            /** Set when the bits did NOT arrive — shown beside the kit, never
             *  instead of it (§4.5's expired-condition path). */
            let walletNotice: string | null = null;
            /** What became of "Publish my receive address", when it was ticked. */
            let publishNotice: string | null = null;
            /** D27, mirrored by the node; the Worker stores what it is told. */
            /** D27, from the node's config; null until read, or when the node names none (M12, D15). */
            let defaultTokenCaps: DefaultTokenTerms | null = null;
            void fetchRegistrationConfig().then(cfg => { defaultTokenCaps = cfg.defaultToken; });

            const ceremony = createEntropyCeremony({
                onReady: (result) => {
                    ceremonyResult = result;
                    finalizeBtn.style.display = result ? '' : 'none';
                },
                // The wallet key rides the SAME stream as the password and the
                // supersecret, drawn last (§4.9) — so the ceremony that produced
                // this member's credentials produced their money too, and the
                // golden vector pins all of it together. Injected, never
                // imported: `crypto/entropy.ts` must not reach back into
                // `crypto/bitcash.ts` (parent plan §13.1's cycle trap).
                mintWalletKey: (next) => generateWalletKeyFrom(next, TYPE_CASH),
            });
            step.appendChild(ceremony);

            const finalizeBtn = createElement('button', {
                className: 'cs-btn cs-btn--yes cid-auth-submit',
                textContent: 'Finalize credentials',
            });
            finalizeBtn.style.display = 'none';
            step.appendChild(finalizeBtn);
            step.appendChild(statusEl);
            step.appendChild(errorEl);

            // ── publish the receive address? (D4, default OFF) ───────────
            // The one decision the print step asks for. A receive address is a
            // public identifier — it can be paid, never spent — but publishing
            // it links this identity to a wallet for anybody who reads the
            // profile, so it is opt-in and stays opt-in.
            const publishRow = createElement('div', { className: 'cid-auth-checkbox-row' });
            const publishBox = document.createElement('input');
            publishBox.type = 'checkbox';
            publishBox.id = 'cid-publish-fund-address';
            publishBox.checked = false;
            const publishLabel = createElement('label', {
                textContent: 'Publish my receive address so others can pay me',
            });
            publishLabel.setAttribute('for', publishBox.id);
            publishRow.appendChild(publishBox);
            publishRow.appendChild(publishLabel);
            step.appendChild(publishRow);
            const publishHint = createElement('p', {
                className: 'cid-pane-note',
                textContent: 'Your receive address can only receive — it cannot spend. Publishing it '
                    + 'puts it on your public profile; you can publish or withdraw it later in '
                    + 'Settings either way.',
            });
            step.appendChild(publishHint);

            // Where to print is said beside the print button(s), once there is
            // something to print (components/print-button.ts PHYSICAL_PRINTER_NOTE).

            container.appendChild(step);

            finalizeBtn.addEventListener('click', async () => {
                if (!ceremonyResult) return;
                const final = ceremonyResult;
                finalizeBtn.setAttribute('disabled', '');
                errorEl.classList.remove('cid-pane-error--visible');

                try {
                    const cid = shared['cid'] as string;
                    const keyBag = shared['keyBag'] as KeyBag;

                    // 1. Reset card — reuse an already-issued token (bump-on-issue:
                    //    re-issuing would kill a card from a previous print attempt).
                    let sealedToken = shared['resetCardSealedToken'] as string | undefined;
                    let sealedCreatedAt = shared['resetCardCreatedAt'] as string | undefined;
                    if (!sealedToken) {
                        statusEl.textContent = 'Sealing your Account Unlock Sheet...';
                        const cardResp = await accountApi.issueResetCard({
                            emailClaimUuid: shared['emailClaimUuid'] as string,
                            emailEncryptedKey: shared['emailClaimEncKey'] as string,
                            phoneClaimUuid: shared['phoneClaimUuid'] as string,
                            phoneEncryptedKey: shared['phoneClaimEncKey'] as string,
                        });
                        sealedToken = cardResp.sealedToken;
                        sealedCreatedAt = cardResp.createdAt;
                        shared['resetCardSealedToken'] = sealedToken;
                        shared['resetCardCreatedAt'] = sealedCreatedAt;
                        stashSync(shared, { resetCardSealedToken: sealedToken, resetCardCreatedAt: sealedCreatedAt });
                    }

                    // 2. The shared finalize sequence (auth/finalize-credentials.ts):
                    //    append the ceremony's `xrpl_master` unless the bag already
                    //    carries one (resume rule §13.1), re-encrypt the bag under
                    //    the FINAL supersecret, replace it server-side, copy the new
                    //    entry onto this device, and set the final auth hash last.
                    stashSync(shared, { finalizeStarted: true });
                    // The Authenticator Key (round 8, §15.6). RESUME RULE: once
                    // the node holds it, never PUT again — a second PUT rotates
                    // the secret and voids the sheet already printed. The stash
                    // remembers both the flag and the secret, and the kit prints
                    // the stash's secret on every reprint.
                    const priorTotp = readStashedTotpSecret(shared);
                    const totpAlreadyStored = loadOnboardingStash()?.totpStored === true && priorTotp !== null;
                    const totpSecret = totpAlreadyStored ? priorTotp : final.totpSecret;
                    // The Recovery Kit's SET (round 9, §16.6). Minted BEFORE the
                    // bag write so it lands in the bag the node stores AND in
                    // the bundle the kit exports, which is what makes every
                    // sheet reprintable from Settings later. finalizeCredentials
                    // discards this mint when the bag already carries one — a
                    // resume must print the set that is already on the server.
                    const kitChoices = readKitChoices(shared);
                    const mintedKitEntry = kitEntryFromChoices(kitChoices, PERSONAL_KIT_DEFAULTS);
                    const finalized = await finalizeCredentials({
                        cid,
                        bag: keyBag,
                        supersecret: final.supersecret,
                        password: final.password,
                        xrplSeedEntropy: final.xrplSeedEntropy,
                        totpSecret,
                        totpAlreadyStored,
                        recoveryKitEntry: mintedKitEntry,
                        // The ceremony's wallet key (§4.8): appended as
                        // `bitcash_wallet` unless the bag already carries one —
                        // a resumed finalize must print the wallet that is
                        // already in the account, never mint a second.
                        bitcashWalletKey: final.bitcashWalletKey ?? null,
                        device: deviceCopyTarget(shared),
                        onStatus: (message) => { statusEl.textContent = message; },
                    });
                    // The bag the account now has — the XRPL sheet prints ITS key,
                    // a reprint reuses it, and a retry after a failure below must
                    // not mint a second one.
                    shared['keyBag'] = finalized.bag;
                    keys.load(finalized.bag);
                    if (finalized.totpStored && totpSecret) {
                        shared['totpSecret'] = base32Encode(totpSecret);
                    }
                    stashSync(shared, { finalizeStarted: true, totpStored: finalized.totpStored });
                    // "No local storage" mode holds no keys here by choice — only
                    // a device that DOES hold keys and missed the copy is a gap.
                    if (finalized.deviceCopyMissing && !deviceStorage.isNoLocalStorageMode(cid)) {
                        console.warn('[registration] the XRPL master key was not copied to this device — '
                            + 'its first use here will ask for the supersecret');
                    }

                    // 2b. The money leg (§4.5). AFTER set-authhash, which
                    //     `finalizeCredentials` did last, and BEFORE the kit,
                    //     which prints the receive address this produces.
                    //
                    //     NOTHING HERE CAN FAIL THE PRINT. Every call returns an
                    //     outcome; a Worker that is down, a lock past its
                    //     `notAfter`, a rate limiter — all of them leave the
                    //     member with their credentials and a sentence
                    //     explaining the money.
                    if (finalized.bitcashWallet) {
                        shared['bitcashWalletKey'] = finalized.bitcashWallet.key;
                        const prior = readBitsStash({
                            ...(loadOnboardingStash()?.shared ?? {}), ...shared,
                        });
                        // The ledger the node names, the wallet's ref, and the
                        // address and token codes, written down BEFORE any ledger
                        // call (apps/bitcash/docs/plans/bitcash-v2.md M12): no ledger, no money leg.
                        const plan = await planWalletSetup({
                            walletKey: finalized.bitcashWallet.key, stash: shared,
                            stashKeys: { fund: 'bitsFundCode', token: 'bitsTokenCode' },
                        });
                        stashSync(shared);
                        // The default token's terms: caps and service types (it is minted unbound, §9.3).
                        const tokenTerms = defaultTokenCaps ?? (await fetchRegistrationConfig()).defaultToken;
                        const walletSetup: WalletSetupResult = plan === null
                            ? { release: null, receiveAddr: null, defaultToken: null, walletMissing: true }
                            : await setUpWallet({
                                walletKey: finalized.bitcashWallet.key,
                                walletRef: plan.walletRef,
                                networkId: plan.networkId,
                                lock: await lockForThisRegistration(shared),
                                caps: tokenTerms,
                                instruments: plan.instruments,
                                alreadyReleased: prior.bitsReleased,
                                alreadyTokenIssued: prior.defaultTokenIssued,
                                onStatus: (message) => { statusEl.textContent = message; },
                            }, { worker: bitcashWorker() });
                        await recordWalletSetup(shared, cid, walletSetup, {
                            walletRef: plan?.walletRef, serviceTypes: tokenTerms?.serviceTypes ?? [],
                        });
                        if (publishBox.checked && walletSetup.receiveAddr) {
                            statusEl.textContent = 'Publishing your receive address…';
                            const published = await publishReceiveAddress({
                                receiveAddr: walletSetup.receiveAddr,
                                attrPublicKey: aes.fromBase64(
                                    (shared['publicKeys'] as Record<string, string>)['attr_encrypt']),
                                // The sharer's half of the ECDH that re-wraps the
                                // claim key for each operator. Read from the bag
                                // the account now HAS, not from the ceremony.
                                attrPrivateKey: aes.fromBase64(getKeyBagEntry(
                                    shared['keyBag'] as KeyBag, 'attr_encrypt')!.privateKey),
                            });
                            shared['publishFundAddress'] = published.status === 'published';
                            publishNotice = publishOutcomeNotice(published);
                        }
                        stashSync(shared);
                        // no ledger, no money leg, nothing to say
                        walletNotice = plan === null ? null : walletSetupNotice(walletSetup);
                    }

                    // 3. Optional recovery kit — the sheets are evaluated from
                    //    the SET the bag now holds (never from fresh
                    //    randomness, §16.6), against the FINAL supersecret and
                    //    the FINAL bag (XRPL key and kit entry included).
                    let kit: RecoveryKit | null = null;
                    if (kitChoices.optIn && finalized.recoveryKitEntry) {
                        statusEl.textContent = 'Creating your recovery sheets...';
                        const publicKeys = shared['publicKeys'] as Record<string, string>;
                        const entry = decodeRecoveryKitEntry(finalized.recoveryKitEntry.privateKey);
                        // The kit's bundle carries the account's blobs as-is
                        // (cid-encrypted-storage §4.2). Best-effort HERE, and
                        // only here: a throw would strand a registration that
                        // has already written the bag and the password, and a
                        // missing blob costs application state, not identity.
                        // The explicit backup actions in Settings let the same
                        // failure through, because there the file IS the goal.
                        let kitBlobs: BundleBlob[] = [];
                        try {
                            kitBlobs = await collectBlobsForBundle(blobApi);
                        } catch (err) {
                            console.warn('[registration] could not collect account blobs '
                                + 'for the recovery kit bundle:', err);
                        }
                        kit = await buildRecoveryKit({
                            entry,
                            cid,
                            supersecret: final.supersecret,
                            cardExtraCopies: kitExtraCopiesFor(kitChoices, entry.totalSharesN),
                            bag: finalized.bag,
                            blobs: kitBlobs,
                            publicKeys: finalized.bag.entries.map(e => ({
                                keyId: e.keyId,
                                algorithm: e.algorithm,
                                // Bag-only ids (xrpl_master, recovery_kit) have no
                                // published public half by design — '' is the marker.
                                publicKey: publicKeys[e.keyId] ?? '',
                                keyIndex: e.keyIndex,
                                status: e.status,
                            })),
                        });
                        try {
                            await accountApi.putRecoveryKit({
                                recoverySetId: kit.recoverySetId,
                                thresholdK: kit.thresholdK,
                                totalSharesN: kit.totalSharesN,
                                bundleFingerprint: kit.bundle.bundle_fingerprint,
                            });
                        } catch {
                            // The kit itself is complete — the server record only
                            // powers reminders. Non-fatal.
                        }
                    }

                    // A ticked box with no address to publish (no wallet) says so too.
                    if (publishBox.checked && publishNotice === null) publishNotice = publishOutcomeNotice(null);

                    // 4. The document set is ready; printing is a click of its
                    //    own (components/print-button.ts). The print window must
                    //    open inside a click, and THIS click's ran out long ago —
                    //    the sequence above takes seconds to a minute, and Firefox
                    //    blocked the window (staging test 2026-10-16). Whatever the
                    //    kit needs is fetched now, never inside that click.
                    await refreshStashedWalletKey(shared);
                    const sealed = sealedToken!;
                    const printKit = (): Promise<void> => {
                        const { pages, copySheets, print } = buildPages(shared, final, sealed, kit);
                        return printBooklet(pages, copySheets, print);
                    };

                    statusEl.textContent = '';
                    for (const notice of [walletNotice, publishNotice]) {
                        if (notice) step.appendChild(createElement('p', { className: 'cid-pane-note', textContent: notice }));
                    }
                    finalizeBtn.style.display = 'none';
                    ceremony.style.display = 'none';
                    // The publish choice was read by Finalize: from here on the box
                    // would only look like a choice still open (staging test
                    // 2026-10-15: it stayed on every later screen).
                    publishRow.style.display = 'none';
                    publishHint.style.display = 'none';
                    // The exact size, now that the document exists (the Terms step
                    // could only say "about").
                    const sized = buildPages(shared, final, sealed, kit);
                    const pageCount = documentSetPageCount(sized.pages, sized.copySheets, sized.print);
                    renderPrintStage(step, {
                        intro: `Your credentials are set. Print the document set now — ${pageCount} pages. `
                            + 'The next step asks you to type back a line from it.',
                        label: 'Print document set',
                        print: printKit,
                        onPrinted: () => renderTypeBackGate(step, shared, final, printKit, ctx),
                    });
                } catch (err) {
                    finalizeBtn.removeAttribute('disabled');
                    statusEl.textContent = '';
                    errorEl.textContent = err instanceof Error ? err.message : 'Finalize failed';
                    errorEl.classList.add('cid-pane-error--visible');
                }
            });
        },
    };
}

/** The Authenticator Key the node already holds for this registration, read
 *  back out of the stash's `shared` map (base32). Null when there is none — a
 *  pre-round-8 stash, or a run that has not finalized yet. */
function readStashedTotpSecret(shared: Record<string, unknown>): Uint8Array | null {
    const value = shared['totpSecret'];
    if (typeof value !== 'string' || !value) return null;
    try {
        return base32Decode(value);
    } catch {
        return null;
    }
}

/** Assemble the Membership Kit for one print: the numbered master pages (cover,
 *  introduction, sheets) plus the additional recovery-sheet copies, which ride
 *  after them unnumbered — and the layout that ties the two together with the
 *  Membership Agreement appended at print time.
 *
 *  The layout is computed BEFORE the pages, because the pages need it: the
 *  introduction prints the table of contents from it and every sheet prints the
 *  consent line naming the agreement's page range. Every section here is one
 *  physical A4 page — the page builders are budgeted for that. */
function buildPages(
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    sealedTokenB64: string,
    kit: RecoveryKit | null,
): { pages: Content[][]; copySheets: CopySheet[]; print: BookletPrintOptions } {
    const cid = shared['cid'] as string;
    const header: AccountHeaderData = {
        cid,
        accountTypeLabel: 'Personal',
        version: formatVersion(new Date()),
    };
    const contact = {
        email: shared['emailAddress'] as string | undefined,
        phone: shared['phoneNumber'] as string | undefined,
    };

    // The XRPL master key is in the bag by the time this runs: the ceremony
    // minted it and the finalize helper appended it (round 6, §13.1), or a
    // resumed bag already carried one — either way the sheet prints the key
    // that is actually in the account. A bag without one (no ceremony value)
    // resumes without the sheet rather than failing.
    const keyBag = shared['keyBag'] as KeyBag;
    const xrplEntry = getKeyBagEntry(keyBag, XRPL_MASTER_KEY_ID);
    const xrplEntropy = xrplEntry ? aes.fromBase64(xrplEntry.privateKey) : null;

    // The Authenticator Key sheet prints only what the NODE holds (§15.6): the
    // stash records the secret when finalize stored it, and a reprint prints
    // that same value rather than re-minting one nobody's login would check.
    const totpSecret = readStashedTotpSecret(shared);

    // The bitcash wallet (§4.8, §7; cid-encrypted-storage §7.2). Like the XRPL
    // key it is read off what the ACCOUNT holds rather than off the ceremony
    // result, so a resumed print and a reprint both show the key that is really
    // there — but "what the account holds" is now the `cid/bitcash-wallets`
    // document, refreshed into the stash by `refreshStashedWalletKey` just
    // before this runs. An account without one prints no sheet rather than
    // failing.
    const walletKey = stashedWalletKey(shared);
    // The receive address is Worker state, not a derivative of the key, so it
    // comes from the stash the money leg wrote. Absent in waived mode and on a
    // resume that has not reached the address step — the sheet is built to
    // print without the block.
    const receiveAddress = typeof shared['bitsFundAddress'] === 'string'
        ? shared['bitsFundAddress'] as string : null;

    const title = bookletTitle('personal');
    const layout = personalKitLayout(
        kit, xrplEntropy !== null, totpSecret !== null, walletKey !== null);
    // The node that holds this account (M7a): the sheet names its origin.
    const nodeHost = nodeHostFor(cid);

    const recoveryKit = kit ? { thresholdK: kit.thresholdK, totalSharesN: kit.totalSharesN } : null;
    const pages: Content[][] = [
        buildCoverPage({ title, header, recoveryKit }),
        buildFrontPage({ layout, recoveryKit }),
        buildCredentialsPage({
            header,
            password: final.password,
            supersecret: final.supersecret,
            email: contact.email,
            phone: contact.phone,
            recoveryKit: recoveryKit ?? undefined,
        }),
        buildResetCardPage({
            header,
            sealedToken: aes.fromBase64(sealedTokenB64),
            nodeHost,
            contact,
        }),
    ];
    if (xrplEntropy) {
        pages.push(buildXrplKeyPage({
            header,
            address: deriveXrplAddress(xrplEntropy),
            secret: encodeXrplSeed(xrplEntropy),
        }));
    }
    if (totpSecret) {
        pages.push(buildAuthenticatorKeyPage({ header, secret: totpSecret }));
    }
    // After the Authenticator Key sheet, before the Recovery Sheets (§7).
    if (walletKey) {
        pages.push(buildBitcashWalletPage({
            header, walletKey, receiveAddress, bitsPaidIn: shared['bitsReleased'] === true,
        }));
    }
    const copySheets: CopySheet[] = [];
    if (kit) {
        pages.push(...buildRecoveryCardPages(header, kit, contact, { nodeHost }));
        copySheets.push(...buildRecoveryCardCopySheets(header, kit, contact, { nodeHost }));
    }
    return {
        pages,
        copySheets,
        print: { title, totalPages: layout.totalPages, terms: TERMS_ASSET, coverArtwork: true },
    };
}

/** The personal kit's page plan. Section titles are the printed page titles —
 *  the introduction's contents table is built straight from them; the
 *  recovery sheets are one section each and the copies are listed by their
 *  footer titles (2026-09-08). */
function personalKitLayout(
    kit: RecoveryKit | null, xrplSheet: boolean, authenticatorSheet: boolean,
    bitcashSheet: boolean,
): KitLayout {
    const sections = [
        { title: 'Cover', pages: 1, inToc: false },
        { title: 'Introduction', pages: 1 },
        { title: 'Credentials', pages: 1 },
        { title: 'Account Unlock Sheet', pages: 1 },
    ];
    if (xrplSheet) sections.push({ title: 'XRPL Master Key', pages: 1 });
    // Round 8 (§15.6): after the XRPL sheet, before the Recovery Sheets.
    if (authenticatorSheet) sections.push({ title: 'Authenticator Key', pages: 1 });
    // Round 10 (registration-wallets §7): after the Authenticator Key sheet,
    // still before the Recovery Sheets.
    if (bitcashSheet) sections.push({ title: BITCASH_WALLET_PAGE_TITLE, pages: 1 });
    if (kit) sections.push(...recoverySheetSections(kit));
    return buildKitLayout({
        sections,
        copySheets: kit ? recoveryCopyTitles(kit) : [],
        terms: { title: TERMS_ASSET.title, pages: TERMS_ASSET.pages },
    });
}

/** The type-back print gate (§3.2 step 6): one supersecret line retyped from
 *  the paper proves a legible print exists. Passing it marks the account's
 *  onboarding complete server-side (§8.5) and only THEN wipes the stash. */
function renderTypeBackGate(
    step: HTMLElement,
    shared: Record<string, unknown>,
    final: EntropyCeremonyResult,
    /** Prints the same document set again — started synchronously in its click. */
    printKit: () => Promise<void>,
    ctx: WizardStepContext,
): void {
    const gate = createElement('div', { className: 'cid-wizard-step' });
    const lineNo = 1 + Math.floor(Math.random() * 4);
    const cleaned = final.supersecret.replace(/[-\s]/g, '');
    const expected = cleaned.slice((lineNo - 1) * 12, lineNo * 12);

    gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Confirm Your Print' }));
    gate.appendChild(createElement('p', {
        className: 'cid-wizard-text',
        textContent: `Take the printed document set and type LINE ${lineNo} of the supersecret exactly as it `
            + 'appears on the Credentials page. This confirms the print is complete and legible — '
            + 'after this step, the credentials exist nowhere but on your paper.',
    }));

    const fieldWrapper = createElement('div', { className: 'cid-auth-field' });
    const input = document.createElement('input');
    input.type = 'text';
    input.className = 'cid-auth-input';
    input.placeholder = `Supersecret line ${lineNo} (12 characters)`;
    input.autocomplete = 'off';
    input.spellcheck = false;
    fieldWrapper.appendChild(input);
    gate.appendChild(fieldWrapper);

    const errorEl = createElement('div', { className: 'cid-pane-error' });
    gate.appendChild(errorEl);

    const confirmBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Confirm print',
    });
    confirmBtn.addEventListener('click', () => {
        const typed = input.value.replace(/[-\s]/g, '');
        if (typed !== expected) {
            errorEl.textContent = typed.length !== 12
                ? 'That is not 12 characters — type the full line, dashes optional.'
                : 'That does not match the printed line — check for look-alike characters and try again, or reprint.';
            errorEl.classList.add('cid-pane-error--visible');
            return;
        }
        void confirmCompletion();
    });
    gate.appendChild(confirmBtn);

    // Shown only after a failed completion call: repeats the CALLS, not the
    // gate — the typed line already matched, so re-typing it proves nothing.
    const retryBtn = createElement('button', {
        className: 'cs-btn cs-btn--yes cid-auth-submit',
        textContent: 'Retry',
    });
    retryBtn.style.display = 'none';
    retryBtn.addEventListener('click', () => { void confirmCompletion(); });
    gate.appendChild(retryBtn);

    gate.appendChild(createPrintButton({
        label: 'Print again', className: 'cs-btn cs-btn--default', print: printKit, errorEl,
    }));

    step.appendChild(gate);

    /** Tell the server onboarding is done, THEN wipe the stash (§8.5 — an
     *  account that never completes is purged after 7 days, so the stash must
     *  survive a failed call for the resume to land back on this gate). */
    async function confirmCompletion(): Promise<void> {
        errorEl.classList.remove('cid-pane-error--visible');
        confirmBtn.setAttribute('disabled', '');
        retryBtn.setAttribute('disabled', '');
        retryBtn.style.display = 'none';
        input.disabled = true;
        const priorLabel = confirmBtn.textContent;
        confirmBtn.textContent = 'Confirming with the server…';
        try {
            await completeOnboardingThenWipe(
                [() => accountApi.completeOnboarding()],
                clearRegistrationState,
            );
        } catch (err) {
            const message = err instanceof Error ? err.message : 'unknown error';
            errorEl.textContent = `Could not confirm completion with the server — ${message}. Retry.`;
            errorEl.classList.add('cid-pane-error--visible');
            confirmBtn.style.display = 'none';
            confirmBtn.textContent = priorLabel;
            retryBtn.removeAttribute('disabled');
            retryBtn.style.display = '';
            return;
        }
        // The optional Login code step (§15.6): after the gate has passed and
        // the account is marked complete, never before — it must not be able to
        // block the completion it follows.
        const totpSecret = readStashedTotpSecret(shared);
        if (totpSecret) {
            gate.innerHTML = '';
            // The device checkbox of device-setup-restructure §6.5 needs to know
            // whether this browser stored a key copy at all — "No local storage"
            // registered no device row, so the choice is hidden there.
            const loginCodeCid = shared['cid'] as string;
            renderLoginCodeStep(gate, {
                cid: loginCodeCid,
                secret: totpSecret,
                onDone: renderDone,
                deviceToken: deviceStorage.getDeviceToken(loginCodeCid),
            });
            return;
        }
        renderDone();
    }

    function renderDone(): void {
        gate.innerHTML = '';
        gate.appendChild(createElement('h3', { className: 'cid-wizard-heading', textContent: 'Registration Complete' }));
        gate.appendChild(createElement('p', {
            className: 'cid-wizard-text',
            textContent: 'Your document set is confirmed. Store it safely — and distribute any recovery '
                + 'sheets to their custodians.',
        }));
        const finishBtn = createElement('button', {
            className: 'cs-btn cs-btn--yes cid-auth-submit',
            textContent: 'Finish',
        });
        finishBtn.addEventListener('click', () => ctx.wizard.next());
        gate.appendChild(finishBtn);
    }
}

// ── the print step's money leg (registration-wallets §4.5) ──────────────────

/**
 * Which lock this registration's bits are sitting in, and the seed that opens
 * it — or undefined when there is none to open.
 *
 * TWO PATHS, one shape (§4.5):
 *
 *   * SELF-USE — the member holds the voucher, whether they typed it at the
 *     entry page or bought it one step ago with "pay and continue". The lock is
 *     `L(pkV, 0)` and the voucher's seed signs.
 *   * INVITEE — somebody personalized a voucher for them. The bits were moved
 *     off the voucher onto `L(pkI, 0)` at that moment, so the INVITATION's seed
 *     is the only thing that opens them; the voucher's key is the buyer's
 *     reclaim path and this member never had it.
 *
 * Both derive from a code the stash is carrying, so a resume re-derives them
 * rather than remembering key material across a reload.
 *
 * Waived mode (D9) and a registration paid for by proof alone reach neither
 * branch: no voucher, no lock — and on the v2 ledger a wallet exists only from
 * its first credit, so it takes no address and no token until a gift arrives
 * (apps/bitcash/docs/plans/bitcash-v2.md M12).
 */
async function lockForThisRegistration(
    shared: Record<string, unknown>,
): Promise<{ lockKey: string; seed: Uint8Array } | undefined> {
    const invitationCode = shared['invitationCode'] as string | undefined;
    if (invitationCode) {
        const secrets = await deriveInvitationSecrets(invitationCode);
        return { lockKey: lockKeyFor(secrets.publicKey, 0), seed: secrets.seed };
    }

    const voucherCode = shared['voucherCode'] as string | undefined;
    if (voucherCode) {
        const secrets: VoucherSecrets = await deriveVoucherSecrets(voucherCode);
        return { lockKey: lockKeyFor(secrets.publicKey, 0), seed: secrets.seed };
    }
    return undefined;
}

/**
 * Write down what the money leg produced, and cache the default token.
 *
 * The STASH fields are the resume contract of §4.5: `bitsReleased` stops a
 * second release, `defaultTokenIssued` stops a second MINT (every token past
 * the first costs bits), and `bitsWalletRef`/`bitsFundAddress` are what the kit
 * and the panes read. All of them are written only after the call they describe
 * returned.
 *
 * The TOKEN goes into an encrypted device slot, never into the key bag (D12):
 * it is a cache of Worker state, and the Worker's list is the truth. A device
 * that cannot hold it (no local storage, no device key yet) simply does not,
 * and the key-grant handler asks for a supersecret the first time a module
 * wants a token.
 */
async function recordWalletSetup(
    shared: Record<string, unknown>,
    cid: string,
    setup: WalletSetupResult,
    token: { walletRef?: string; serviceTypes: readonly string[] },
): Promise<void> {
    if (setup.release?.status === 'released') {
        shared['bitsReleased'] = true;
        shared['bitsWalletRef'] = setup.release.walletRef;
    }
    if (setup.receiveAddr) shared['bitsFundAddress'] = setup.receiveAddr;
    if (setup.defaultToken) {
        shared['defaultTokenIssued'] = true;
        // The key-grant handler's own entry: the token and the scope it was minted with (§9.3),
        // never the wallet key.
        await setDeviceSlot(DEFAULT_TOKEN_SLOT, mintedDefaultTokenEntry({
            token: setup.defaultToken, walletRef: token.walletRef, serviceTypes: token.serviceTypes,
        }), cid);
    }
}

/**
 * Refresh the stashed bitcash wallet key from the account's wallet document
 * before a print.
 *
 * §13.1's rule is "print what the account really has", and since
 * cid-encrypted-storage §7.2 what it really has is `cid/bitcash-wallets`, not a
 * key-bag entry. The stash — written by the finalize step from the document it
 * adopted or created — stays the fallback: a tab that cannot read the blob must
 * still print the key the voucher's bits were released into.
 */
async function refreshStashedWalletKey(shared: Record<string, unknown>): Promise<void> {
    try {
        const held = await registrationWalletKey();
        if (held) shared['bitcashWalletKey'] = held;
    } catch {
        // `registrationWalletKey` already swallows its own failures; this is
        // belt and braces, because nothing about a print may throw here.
    }
}

/** The wallet key the kit sheet prints, or null when the account has none. */
function stashedWalletKey(shared: Record<string, unknown>): string | null {
    const key = shared['bitcashWalletKey'];
    return typeof key === 'string' && key ? key : null;
}
